// The interface language: Dutch by default, English on request, remembered.
//
// Deliberately small. Two languages, one catalogue each (en.ts, nl.ts), and
// nl.ts is typed against en.ts so a key added to one and forgotten in the other
// fails the typecheck rather than showing a raw key on screen.
//
// A message is a string with {placeholders}, or a function where Dutch and
// English disagree about more than words (plurals, word order).
//
// The choice lives in localStorage under LOCALE_KEY. Storage can be missing or
// throw (a private window, blocked site data); then the app is simply Dutch
// and the switch still works for the session.
//
// Screens that have not been translated yet still carry their English text
// inline. See docs/i18n.md for which ones.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { en, type MessageKey, type Messages, type Vars } from './en.ts';
import { nl } from './nl.ts';

export type Locale = 'nl' | 'en';
export type { MessageKey, Vars };

export const LOCALES: readonly Locale[] = ['nl', 'en'];
export const DEFAULT_LOCALE: Locale = 'nl';
export const LOCALE_KEY = 'bk.locale';

const CATALOGUES: Readonly<Record<Locale, Messages>> = { en, nl };

export function readStoredLocale(): Locale {
  try {
    const stored = globalThis.localStorage?.getItem(LOCALE_KEY);
    return stored === 'en' || stored === 'nl' ? stored : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

function storeLocale(locale: Locale): void {
  try {
    globalThis.localStorage?.setItem(LOCALE_KEY, locale);
  } catch {
    // Not remembered; still switched for this session.
  }
}

export type Translate = (key: MessageKey, vars?: Vars) => string;

export function translate(locale: Locale, key: MessageKey, vars: Vars = {}): string {
  const message = CATALOGUES[locale][key];
  if (typeof message === 'function') return message(vars);
  return message.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name];
    return value === undefined ? whole : String(value);
  });
}

interface LocaleState {
  readonly locale: Locale;
  readonly setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleState | null>(null);

export function LocaleProvider({ children, initial }: { readonly children: ReactNode; readonly initial?: Locale }): ReactNode {
  const [locale, setState] = useState<Locale>(() => initial ?? readStoredLocale());
  const setLocale = useCallback((next: Locale) => {
    storeLocale(next);
    setState(next);
  }, []);
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/**
 * The current language. Outside a provider (a screen mounted on its own in a
 * test) it is the stored choice and cannot be changed; the app always has one.
 */
export function useLocale(): LocaleState {
  const context = useContext(LocaleContext);
  return context ?? { locale: readStoredLocale(), setLocale: () => undefined };
}

export function useT(): Translate {
  const { locale } = useLocale();
  return useCallback((key: MessageKey, vars?: Vars) => translate(locale, key, vars), [locale]);
}

/** The BCP 47 tag for Intl formatting in the current language. */
export function intlTag(locale: Locale): string {
  return locale === 'nl' ? 'nl-NL' : 'en-GB';
}
